// * generally want to limit the imports of corso-types but this component is built specifically for a type from corso-types
import { CrewUi } from 'corso-types';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import Input from './form-controls/Input';
import { MultiSelect } from './form-controls/MultiSelect';
import { Select, SelectItem } from './form-controls/Select';
import TextArea from './form-controls/TextArea';
import MediaDisplay from './MediaDisplay';

type CustomField =
  CrewUi.CollectCustomFieldEvent['params']['customFields'][number];

const stringArrayDefaultValue = (defaultValue?: string): string[] => {
  if (!defaultValue) return [];

  return defaultValue
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
};

function CustomFieldValue({
  field: customField,
  defaultValue,
  className,
}: {
  field: CustomField;
  defaultValue?: string;
  className?: string;
}) {
  return match(customField)
    .with({ valueType: 'MultiSelect' }, (field) => (
      <MultiSelect
        id={`${field.id}`}
        name={`${field.id}[value]`}
        label={field.displayName}
        className={className}
        required={field.required}
        description={field.description}
        defaultValue={stringArrayDefaultValue(defaultValue)}
        options={field.options.map((option) => ({
          label: option,
          value: option,
        }))}
      />
    ))
    .with({ valueType: 'Select' }, (field) => (
      <Select
        id={`${field.id}`}
        className={className}
        required={field.required}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      >
        {field.options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </Select>
    ))
    .with({ valueType: 'Text' }, (field) => (
      <TextArea
        id={`${field.id}`}
        className={className}
        required={field.required}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      />
    ))
    .with({ valueType: 'Date' }, (field) => (
      <Input
        type="date"
        id={`${field.id}`}
        className={className}
        required={field.required}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      />
    ))
    .with({ valueType: 'Number' }, (field) => (
      <Input
        type="number"
        id={`${field.id}`}
        className={className}
        required={field.required}
        description={field.description}
        name={`${field.id}[value]`}
        label={field.displayName}
        defaultValue={defaultValue}
      />
    ))
    .exhaustive();
}

function CustomFieldType({ field }: { field: CustomField }) {
  return (
    <input
      type="hidden"
      name={`${field.id}[type]`}
      value={field.valueType}
      readOnly
    />
  );
}

export default function CustomField({
  field,
  defaultValue,
  className,
}: {
  field: CustomField;
  defaultValue?: string;
  className?: string;
}) {
  return (
    <>
      <div className={twMerge('flex flex-col gap-2', className)}>
        <CustomFieldValue field={field} defaultValue={defaultValue} />
        {!!field.referenceImages?.length && (
          <div className="flex flex-wrap items-center gap-2">
            {field.referenceImages.map(({ url, caption }) => (
              <MediaDisplay
                key={url}
                expandable
                type="image"
                src={url}
                alt={caption ?? field.displayName}
                caption={caption}
                className="aspect-3/2 w-[74px] *:object-cover sm:w-[84px]"
              />
            ))}
          </div>
        )}
      </div>
      <CustomFieldType field={field} />
    </>
  );
}
